import { Component } from '@theme/component';

/**
 * Gourmet Pantry product panel (sections/gp-product-main.liquid).
 *
 * The size cards are name="id" radios inside the product form, so the form submits the
 * chosen variant without this script. This component keeps the rest of the panel in step:
 * variant price blocks, the product form's variantId ref, Add to cart availability, the
 * quantity selector's variant, the gallery photo, the ?variant= URL, and the sticky
 * add-to-cart bar.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [variantDetails] - Blocks shown only for their data-variant-id.
 * @property {HTMLElement} [stickyBar] - Sticky add-to-cart bar.
 * @property {HTMLButtonElement} [stickyButton] - Sticky bar button.
 *
 * @extends {Component<Refs>}
 */
class GpProductComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#observeAddButton();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeAddButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** @returns {HTMLButtonElement | null} */
  get #addButton() {
    const button = this.querySelector('product-form-component button[name="add"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Handles a change of size radio.
   * @param {Event} event
   */
  handleVariantChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const variantId = input.value;
    const available = input.dataset.available === 'true';

    for (const element of this.refs.variantDetails ?? []) {
      element.hidden = element.dataset.variantId !== variantId;
    }

    // Purchase option prices live inside product-form-component, whose refs are its own,
    // so they are found by class instead.
    for (const element of this.querySelectorAll('.gp-pdp__variant-only')) {
      if (element instanceof HTMLElement) element.hidden = element.dataset.variantId !== variantId;
    }

    const variantIdInput = this.querySelector('product-form-component input[ref="variantId"]');
    if (variantIdInput instanceof HTMLInputElement) variantIdInput.value = variantId;

    const addButton = this.#addButton;
    if (addButton) addButton.disabled = !available;
    if (this.refs.stickyButton) this.refs.stickyButton.disabled = !available;

    // Show the chosen size's photo: the gallery thumbnails are radios carrying their media id.
    const mediaId = input.dataset.mediaId;
    if (mediaId) {
      const thumb = this.querySelector(`.gp-pdp__thumb-input[data-media-id="${CSS.escape(mediaId)}"]`);
      if (thumb instanceof HTMLInputElement) thumb.checked = true;
    }

    const quantitySelector = this.querySelector('product-form-component quantity-selector-component');
    if (quantitySelector instanceof HTMLElement) quantitySelector.dataset.variantId = variantId;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState(window.history.state, '', url);
  }

  /** Adds to cart from the sticky bar through the main product form. */
  handleStickyAdd() {
    const addButton = this.#addButton;
    if (!addButton || addButton.disabled) return;

    addButton.click();
  }

  #observeAddButton() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    const { stickyBar } = this.refs;
    const addButton = this.#addButton;
    if (!stickyBar || !addButton) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      // Show the bar once the main button has scrolled above the viewport.
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyBar.hidden = !show;
    });
    this.#observer.observe(addButton);
  }
}

if (!customElements.get('gp-product-component')) {
  customElements.define('gp-product-component', GpProductComponent);
}
